import { c as _c } from "react/compiler-runtime";
import AutoAwesomeMotion from "@mui/icons-material/AutoAwesomeMotion";
import CallSplit from "@mui/icons-material/CallSplit";
import History from "@mui/icons-material/History";
import PlayCircleOutlined from "@mui/icons-material/PlayCircleOutlined";
import { alpha, Box, Button, CardActionArea, Chip, Container, Grid, Stack, Typography, useTheme } from "@mui/material";
import Link from "next/link";
import React from "react";
import { LANDING_PLAYGROUND_DEMOS } from "#/features/homePage/lib/landingPlaygroundDemos";
import { HomeLandingPythonDecor } from "#/features/homePage/ui/landing/HomeLandingPythonDecor";
import { LandingGlowCard } from "#/features/homePage/ui/landing/LandingGlowCard";
import { LandingReveal } from "#/features/homePage/ui/landing/LandingReveal";
import type { TranslationFunctions } from "#/i18n/i18n-types";
export type HomeLandingSectionsProps = {
  LL: TranslationFunctions;
};
type LandingDemoId = (typeof LANDING_PLAYGROUND_DEMOS)[number]["id"];
function landingDemoLabel(LL: TranslationFunctions, id: LandingDemoId): string {
  switch (id) {
    case "tree":
      return LL.HOME_DEMO_TREE();
    case "graph":
      return LL.HOME_DEMO_GRAPH();
    case "grid":
      return LL.HOME_DEMO_GRID();
    case "trie":
      return LL.HOME_DEMO_TRIE();
    default:
      {
        const _exhaustive: never = id;
        return _exhaustive;
      }
  }
}
function landingDemoSlug(LL: TranslationFunctions, id: LandingDemoId): string {
  switch (id) {
    case "tree":
      return LL.HOME_DEMO_SLUG_INVERT_BINARY_TREE();
    case "graph":
      return LL.HOME_DEMO_SLUG_PATH_IN_GRAPH();
    case "grid":
      return LL.HOME_DEMO_SLUG_SHORTEST_PATH_MATRIX();
    case "trie":
      return LL.HOME_DEMO_SLUG_TRIE_NAME();
    default:
      {
        const _exhaustive: never = id;
        return _exhaustive;
      }
  }
}
export const HomeLandingSections: React.FC<HomeLandingSectionsProps> = t0 => {
  const $ = _c(151);
  const {
    LL
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CallSplit fontSize="small" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== LL) {
    t2 = LL.HOME_PILLAR_VIS_TITLE();
    $[1] = LL;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== LL) {
    t3 = LL.HOME_PILLAR_VIS_BODY();
    $[3] = LL;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      id: "pillar-vis",
      icon: t1,
      title: t2,
      body: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PlayCircleOutlined fontSize="small" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== LL) {
    t6 = LL.HOME_PILLAR_WORKERS_TITLE();
    $[9] = LL;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== LL) {
    t7 = LL.HOME_PILLAR_WORKERS_BODY();
    $[11] = LL;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = {
      id: "pillar-workers",
      icon: t5,
      title: t6,
      body: t7
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <History fontSize="small" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== LL) {
    t10 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[17] = LL;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== LL) {
    t11 = LL.HOME_PILLAR_REPLAY_BODY();
    $[19] = LL;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = {
      id: "pillar-replay",
      icon: t9,
      title: t10,
      body: t11
    };
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AutoAwesomeMotion fontSize="small" />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== LL) {
    t14 = LL.HOME_PILLAR_BENCH_TITLE();
    $[25] = LL;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== LL) {
    t15 = LL.HOME_PILLAR_BENCH_BODY();
    $[27] = LL;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = {
      id: "pillar-bench",
      icon: t13,
      title: t14,
      body: t15
    };
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t12 || $[33] !== t16 || $[34] !== t4 || $[35] !== t8) {
    t17 = [t4, t8, t12, t16];
    $[32] = t12;
    $[33] = t16;
    $[34] = t4;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const capabilities = t17 as const;
  let t18;
  if ($[37] !== LL) {
    t18 = LL.RUN();
    $[37] = LL;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== LL) {
    t19 = LL.HOME_HOW_STEP_2_BODY();
    $[39] = LL;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t18 || $[42] !== t19) {
    t20 = {
      id: "core-run",
      label: t18,
      body: t19
    };
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== LL) {
    t21 = LL.FORWARD();
    $[44] = LL;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== LL) {
    t22 = LL.HOME_HOW_STEP_3_BODY();
    $[46] = LL;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21 || $[49] !== t22) {
    t23 = {
      id: "core-forward",
      label: t21,
      body: t22
    };
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== LL) {
    t24 = LL.REPLAY();
    $[51] = LL;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== LL) {
    t25 = LL.HOME_PILLAR_REPLAY_BODY();
    $[53] = LL;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t24 || $[56] !== t25) {
    t26 = {
      id: "core-replay",
      label: t24,
      body: t25
    };
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== LL) {
    t27 = LL.CALLSTACK();
    $[58] = LL;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== LL) {
    t28 = LL.HOME_PILLAR_VIS_BODY();
    $[60] = LL;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27 || $[63] !== t28) {
    t29 = {
      id: "core-callstack",
      label: t27,
      body: t28
    };
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t20 || $[66] !== t23 || $[67] !== t26 || $[68] !== t29) {
    t30 = [t20, t23, t26, t29];
    $[65] = t20;
    $[66] = t23;
    $[67] = t26;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  const coreLoop = t30 as const;
  let t31;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      py: {
        xs: 6,
        md: 8
      },
      bgcolor: "background.default"
    };
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      mb: 1.5,
      maxWidth: 780
    };
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== LL) {
    t34 = LL.HOME_PILLAR_VIS_TITLE();
    $[73] = LL;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t34) {
    t35 = <LandingReveal sx={t33}><Typography id="landing-capabilities-heading" variant="h3" component="h2">{t34}</Typography></LandingReveal>;
    $[75] = t34;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== capabilities) {
    t36 = <Grid container={true} spacing={3}>{capabilities.map(_temp)}</Grid>;
    $[77] = capabilities;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t35 || $[80] !== t36) {
    t37 = <Box sx={t31}><Container maxWidth="xl" sx={t32}>{t35}{t36}</Container></Box>;
    $[79] = t35;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== theme.appDesign.surfaceLow) {
    t38 = alpha(theme.appDesign.surfaceLow, 0.65);
    $[82] = theme.appDesign.surfaceLow;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t38) {
    t39 = {
      position: "relative",
      overflow: "visible",
      bgcolor: t38
    };
    $[84] = t38;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <HomeLandingPythonDecor />;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      position: "relative",
      zIndex: 1
    };
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      py: {
        xs: 6,
        md: 8
      }
    };
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      mb: 1.5,
      maxWidth: 720
    };
    $[90] = t44;
  } else {
    t44 = $[90];
  }
  let t45;
  if ($[91] !== LL) {
    t45 = LL.HOME_SECTION_HOW_IT_WORKS();
    $[91] = LL;
    $[92] = t45;
  } else {
    t45 = $[92];
  }
  let t46;
  if ($[93] !== t45) {
    t46 = <LandingReveal sx={t44}><Typography variant="h3" component="h2">{t45}</Typography></LandingReveal>;
    $[93] = t45;
    $[94] = t46;
  } else {
    t46 = $[94];
  }
  let t47;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      mb: 4,
      maxWidth: 680
    };
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== LL) {
    t48 = LL.HOME_HOW_STEP_1_BODY();
    $[96] = LL;
    $[97] = t48;
  } else {
    t48 = $[97];
  }
  let t49;
  if ($[98] !== t48) {
    t49 = <LandingReveal sx={t47} staggerMs={70}><Typography variant="body1" color="text.secondary">{t48}</Typography></LandingReveal>;
    $[98] = t48;
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  if ($[100] !== coreLoop || $[101] !== theme.appDesign.surfaceLowest) {
    t50 = <Grid container={true} spacing={2}>{coreLoop.map((item, index_0) => <Grid key={item.id} size={{
        xs: 12,
        sm: 6,
        lg: 3
      }}><LandingGlowCard staggerIndex={index_0 + 4} cardSx={{
          bgcolor: alpha(theme.appDesign.surfaceLowest, 0.72)
        }}><Stack spacing={2} sx={{
            p: 2.5
          }}><Typography variant="overline" color="text.secondary">{String(index_0 + 1).padStart(2, "0")}</Typography><Typography variant="h6">{item.label}</Typography><Typography variant="body2" color="text.secondary" sx={{
              lineHeight: 1.75
            }}>{item.body}</Typography></Stack></LandingGlowCard></Grid>)}</Grid>;
    $[100] = coreLoop;
    $[101] = theme.appDesign.surfaceLowest;
    $[102] = t50;
  } else {
    t50 = $[102];
  }
  let t51;
  if ($[103] !== t46 || $[104] !== t49 || $[105] !== t50) {
    t51 = <Box sx={t42}><Container maxWidth="xl" sx={t43}>{t46}{t49}{t50}</Container></Box>;
    $[103] = t46;
    $[104] = t49;
    $[105] = t50;
    $[106] = t51;
  } else {
    t51 = $[106];
  }
  let t52;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = {
      py: {
        xs: 6,
        md: 8
      }
    };
    $[107] = t52;
  } else {
    t52 = $[107];
  }
  let t53;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      px: {
        xs: 2,
        sm: 3,
        md: 4
      }
    };
    $[108] = t53;
  } else {
    t53 = $[108];
  }
  let t54;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      mb: 1.5
    };
    $[109] = t54;
  } else {
    t54 = $[109];
  }
  let t55;
  if ($[110] !== LL) {
    t55 = LL.HOME_SECTION_TRY_DEMOS();
    $[110] = LL;
    $[111] = t55;
  } else {
    t55 = $[111];
  }
  let t56;
  if ($[112] !== t55) {
    t56 = <LandingReveal sx={t54}><Typography variant="subtitle2" component="h2" color="text.secondary">{t55}</Typography></LandingReveal>;
    $[112] = t55;
    $[113] = t56;
  } else {
    t56 = $[113];
  }
  let t57;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      mb: 1.5,
      maxWidth: 720
    };
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] !== LL) {
    t58 = LL.BROWSE_PROJECTS();
    $[115] = LL;
    $[116] = t58;
  } else {
    t58 = $[116];
  }
  let t59;
  if ($[117] !== t58) {
    t59 = <LandingReveal sx={t57} staggerMs={50}><Typography variant="h3" component="h2">{t58}</Typography></LandingReveal>;
    $[117] = t58;
    $[118] = t59;
  } else {
    t59 = $[118];
  }
  let t60;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = {
      mb: 4,
      maxWidth: 620
    };
    $[119] = t60;
  } else {
    t60 = $[119];
  }
  let t61;
  if ($[120] !== LL) {
    t61 = LL.HOME_TRY_DEMOS_LEAD();
    $[120] = LL;
    $[121] = t61;
  } else {
    t61 = $[121];
  }
  let t62;
  if ($[122] !== t61) {
    t62 = <LandingReveal sx={t60} staggerMs={100}><Typography variant="body1" color="text.secondary">{t61}</Typography></LandingReveal>;
    $[122] = t61;
    $[123] = t62;
  } else {
    t62 = $[123];
  }
  let t63;
  if ($[124] !== LL || $[125] !== theme.appDesign.accent || $[126] !== theme.appDesign.accentSoft) {
    t63 = LANDING_PLAYGROUND_DEMOS.map((t64, demoIndex) => {
      const {
        href,
        id,
        Icon
      } = t64;
      return <Grid key={id} size={{
        xs: 12,
        sm: 6,
        lg: 3
      }}><LandingGlowCard interactive={true} staggerIndex={demoIndex + 8} cardSx={{
          height: "100%"
        }}><CardActionArea component={Link} href={href} sx={{
            p: 2.5
          }}><Stack spacing={2}><Box sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.appDesign.accent, 0.12),
                color: theme.appDesign.accentSoft
              }}><Icon /></Box><Stack spacing={0.5}><Typography variant="subtitle2" color="text.secondary">{LL.HOME_SECTION_TRY_DEMOS()}</Typography><Typography variant="h6">{landingDemoLabel(LL, id)}</Typography></Stack><Typography variant="body2" color="text.secondary">{landingDemoSlug(LL, id)}</Typography></Stack></CardActionArea></LandingGlowCard></Grid>;
    });
    $[124] = LL;
    $[125] = theme.appDesign.accent;
    $[126] = theme.appDesign.accentSoft;
    $[127] = t63;
  } else {
    t63 = $[127];
  }
  let t64;
  if ($[128] !== t63) {
    t64 = <Grid container={true} spacing={2.5}>{t63}</Grid>;
    $[128] = t63;
    $[129] = t64;
  } else {
    t64 = $[129];
  }
  let t65;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = {
      mt: 3,
      textAlign: {
        xs: "center",
        sm: "left"
      }
    };
    $[130] = t65;
  } else {
    t65 = $[130];
  }
  let t66;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = {
      px: 3
    };
    $[131] = t66;
  } else {
    t66 = $[131];
  }
  let t67;
  if ($[132] !== LL) {
    t67 = LL.BROWSE_PROJECTS();
    $[132] = LL;
    $[133] = t67;
  } else {
    t67 = $[133];
  }
  let t68;
  if ($[134] !== t67) {
    t68 = <Box sx={t65}><Button component={Link} href="/playground?view=browse" variant="contained" size="large" sx={t66}>{t67}</Button></Box>;
    $[134] = t67;
    $[135] = t68;
  } else {
    t68 = $[135];
  }
  let t69;
  if ($[136] !== t56 || $[137] !== t59 || $[138] !== t62 || $[139] !== t64 || $[140] !== t68) {
    t69 = <Box sx={t52}><Container maxWidth="xl" sx={t53}>{t56}{t59}{t62}{t64}{t68}</Container></Box>;
    $[136] = t56;
    $[137] = t59;
    $[138] = t62;
    $[139] = t64;
    $[140] = t68;
    $[141] = t69;
  } else {
    t69 = $[141];
  }
  let t70;
  if ($[142] !== t51 || $[143] !== t69) {
    t70 = <Box sx={t41}>{t51}{t69}</Box>;
    $[142] = t51;
    $[143] = t69;
    $[144] = t70;
  } else {
    t70 = $[144];
  }
  let t71;
  if ($[145] !== t39 || $[146] !== t70) {
    t71 = <Box sx={t39}>{t40}{t70}</Box>;
    $[145] = t39;
    $[146] = t70;
    $[147] = t71;
  } else {
    t71 = $[147];
  }
  let t72;
  if ($[148] !== t37 || $[149] !== t71) {
    t72 = <Box component="section" aria-labelledby="landing-capabilities-heading">{t37}{t71}</Box>;
    $[148] = t37;
    $[149] = t71;
    $[150] = t72;
  } else {
    t72 = $[150];
  }
  return t72;
};
function _temp(capability, index) {
  return <Grid key={capability.id} size={{
    xs: 12,
    sm: 6,
    lg: 3
  }}><LandingGlowCard staggerIndex={index}><Stack spacing={1.5} sx={{
        p: 2.5
      }}><Chip size="small" icon={capability.icon} label={capability.title} sx={{
          alignSelf: "flex-start"
        }} /><Typography variant="body2" color="text.secondary" sx={{
          lineHeight: 1.75
        }}>{capability.body}</Typography></Stack></LandingGlowCard></Grid>;
}